import * as React from "react";
import { AlertColor, Box, Stack, Typography } from "@mui/material";
import FastAccessSegment, { FastAccessSegmentButtonProps } from "./fastAccessSegment";
import { useNavigate } from "react-router-dom";
import { useLazyGetResourceByEmailQuery } from "../../../services/cclTokenedResourceApi";
import { useSearchEnterpriseUsersMutation } from "../../../services/cclTokenedEnterpriseUserApi";
import { useGetCclEventsMutation } from "../../../services/cclTokenedSessionApi";
import { updateRegistrationSearchParams } from "../../../app/slices/registrationListConfigSlice";
import { getDateStringAdjusted } from "../../../utilities/dateHelpers";
import { useDispatch } from "react-redux";
import { validateEmail } from "../../../utilities/generalUtilities";
import { FeatureFlag } from "../../../featureFlags";
import CreateSessionDrawer from "../../sessions/createSessionsDrawer/createSessionsDrawer";
import CreateNewUserDrawer from "../../users/createNewUserDrawer";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import { eRoleIds, UserClaimsService } from "../../../services/currentUserService/currentUserService";

interface FastAccessProps {}

const FastAccess: React.FC<FastAccessProps> = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const gridItemWidth: number = 20;
    const gridTitleWidth: number = 15;

    const [tabIndex, setTabIndex] = React.useState<number>(0);

    const features = React.useContext(FeatureFlag);

    const [runSearch, { data: resources, isLoading: resourceLoading, isError: resourceError }] =
        useLazyGetResourceByEmailQuery();

    const [runUserSearch, { data: userData, isLoading: userLoading, isError: userError }] =
        useSearchEnterpriseUsersMutation();

    const [
        runSessionSearch,
        { data: sessionData, isLoading: sessionLoading, isError: sessionError },
    ] = useGetCclEventsMutation();

    const [resourceNotFound, setResouceNotFound] = React.useState<boolean>(false);
    const [userNotFound, setUserNotFound] = React.useState<boolean>(false);
    const [sessionNotFound, setSessionNotFound] = React.useState<boolean>(false);

    const [isBio, setIsBio] = React.useState<boolean>(false);
    const [searchTerm, setSearchTerm] = React.useState<string>("");

    const [openCreateSession, setOpenCreateSession] = React.useState<boolean>(false);
    const [openCreateUser, setOpenCreateUser] = React.useState<boolean>(false);

    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    React.useEffect(() => {
        if (resources && resources[0]) {
            setResouceNotFound(false);
            if (resources.length > 1) {
                navigate(
                    `/resources/${isBio ? 2 : 1}?searchparam=${encodeURIComponent(searchTerm)}`
                );
                setSearchTerm("");
            } else {
                const resourceUrl: string =
                    tabIndex > -1
                        ? `/resource/${resources[0].pkey}/${tabIndex}`
                        : `/resource/${resources[0].pkey}/0`;
                navigate(resourceUrl);
            }
        } else if (resources && !resources[0]) {
            setResouceNotFound(true);
        }

        if (userData && userData[0]) {
            setUserNotFound(false);
            if (userData.length > 1) {
                navigate(`/users?searchparam=${encodeURIComponent(searchTerm)}`);
            } else {
                const userUrl: string =
                    tabIndex > -1
                        ? `/user/?email=${encodeURIComponent(userData[0].email)}&imkey=${
                              userData[0].imKey
                          }&firstName=${userData[0].firstName}&lastName=${
                              userData[0].lastName
                          }&tabIndex=${tabIndex}`
                        : `/user?email=${encodeURIComponent(userData[0].email)}&imkey=${
                              userData[0].imKey
                          }&firstName=${userData[0].firstName}&lastName=${userData[0].lastName}`;
                navigate(userUrl);
            }
        } else if (userData && !userData[0]) {
            setUserNotFound(true);
        }

        if (sessionData && sessionData[0]) {
            setSessionNotFound(false);
            if (sessionData.length > 1) {
                navigate(`/sessions?searchparam=${encodeURIComponent(searchTerm)}`);
                setSearchTerm("");
                return;
            }
            // The Payroll tab (12) is hidden for Licensee-managed sessions, so fall back to the
            // default tab rather than deep-linking into a tab that won't be there.
            const targetTabIndex =
                tabIndex === 12 && sessionData[0].campusKey === "6" ? 0 : tabIndex;
            const sessionUrl: string =
                targetTabIndex > -1
                    ? `/session/${sessionData[0].eventnumber}/${targetTabIndex}`
                    : `/session/${sessionData[0].eventnumber}`;
            navigate(sessionUrl);
        } else if (sessionData && !sessionData[0]) {
            setSessionNotFound(true);
        }
        // eslint-disable-next-line
    }, [resources, userData, sessionData]);

    const resourceCallback = (email: string, tabIndex: number) => {
        setTabIndex(tabIndex);
        setIsBio(false);
        if (validateEmail(email)) {
            setSearchTerm(email);
            runSearch(email);
        } else {
            setSearchTerm("");
            navigate(`/resources/1?searchparam=${encodeURIComponent(email)}`);
        }
    };

    const userCallback = (user: string, tabIndex: number) => {
        if (tabIndex === 99) {
            setOpenCreateUser(true);
            return;
        }
        setTabIndex(tabIndex);
        const payload = {
            keywords: [user],
        };

        if (validateEmail(user)) {
            setSearchTerm(user);
            runUserSearch(payload);
        } else {
            setSearchTerm("");
            navigate(`/users?searchparam=${encodeURIComponent(user)}`);
        }
    };

    const sessionCallback = (sessionId: string, tabIndex: number) => {
        if (tabIndex === 99) {
            setOpenCreateSession(true);
            return;
        }
        setTabIndex(tabIndex);
        setSearchTerm(sessionId);
        const payload = {
            keyword: sessionId,
        };
        runSessionSearch(payload);
    };

    const registrationCallback = (email: string, tabIndex: number) => {
        setTabIndex(0);
        dispatch(
            updateRegistrationSearchParams({
                StartDate: getDateStringAdjusted(-2),
                EndDate: getDateStringAdjusted(2),
                EmailSearch: email,
                IsDateSearchActive: false,
            })
        );
        navigate(`/registrations`);
    };

    const productSalesCallback = (email: string, tabIndex: number) => {
        setTabIndex(0);
        navigate(`/assessments/3?searchparam=${encodeURIComponent(email)}`);
    };

    const handleClose = () => {
        setOpenCreateSession(false);
        setOpenCreateUser(false);
    };

    let sessionButtonList: FastAccessSegmentButtonProps[] = [
        { label: "Open", tabIndex: 0, mode: "primary" },
        { label: "Registrations", tabIndex: 4, mode: "secondary" },
        { label: "Assessments", tabIndex: 10, mode: "secondary" },
        { label: "Files", tabIndex: 7, mode: "secondary" },
        { label: "Resources", tabIndex: 5, mode: "secondary" },
        { label: "Documents", tabIndex: 6, mode: "secondary" },
    ];

    const claimsService = new UserClaimsService();
    
    if (features && features["payroll-admin"]?.active && claimsService.IsUserInAppRole(eRoleIds.ePayrollManager)) {
        let documentsIndex = sessionButtonList.findIndex(button => button.label === "Documents");
        if (documentsIndex !== -1) {
            sessionButtonList.splice(documentsIndex, 0, { label: "Payroll", tabIndex: 12, mode: "secondary" });
        } else {
            sessionButtonList.push({ label: "Payroll", tabIndex: 12, mode: "secondary" });
        }
    }

    const userButtonList: FastAccessSegmentButtonProps[] = [
        { label: "Open", tabIndex: 2, mode: "primary" },
        { label: "Accounts", tabIndex: 0, mode: "secondary" },
        { label: "Logins", tabIndex: 3, mode: "secondary" },
        { label: "Emails", tabIndex: 1, mode: "secondary" },
    ];

    const setSnackbar = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    return (
        <Box sx={{ border: "1px solid #E0E0E0", borderRadius: "4px", padding: 2 }}>
            <Stack direction={"column"}>
                <Typography sx={{ marginLeft: "5px" }} variant="h6" fontWeight={"bold"}>
                    Fast Access
                </Typography>
                <FastAccessSegment
                    title={"Session"}
                    label={"Enter PR Code or ID"}
                    GridItemWidth={gridItemWidth}
                    GridTitleWidth={gridTitleWidth}
                    callbackFunction={sessionCallback}
                    isLoading={sessionLoading}
                    isError={sessionError || sessionNotFound}
                    buttons={sessionButtonList}
                />

                <FastAccessSegment
                    title={"Registration"}
                    label={"Enter Email"}
                    GridItemWidth={gridItemWidth}
                    GridTitleWidth={gridTitleWidth}
                    callbackFunction={registrationCallback}
                    isLoading={false}
                    isError={false}
                    buttons={[{ label: "Search", tabIndex: -1, mode: "primary" }]}
                />

                <FastAccessSegment
                    title={"Product Sale"}
                    label={"Enter Email"}
                    GridItemWidth={gridItemWidth}
                    GridTitleWidth={gridTitleWidth}
                    callbackFunction={productSalesCallback}
                    isLoading={false}
                    isError={false}
                    buttons={[{ label: "Search", tabIndex: -1, mode: "primary" }]}
                />

                <FastAccessSegment
                    title={"Resource"}
                    label={"Enter Email"}
                    GridItemWidth={gridItemWidth}
                    GridTitleWidth={gridTitleWidth}
                    callbackFunction={resourceCallback}
                    isLoading={isBio ? false : resourceLoading}
                    isError={isBio ? false : resourceError || resourceNotFound}
                    buttons={[
                        { label: "Open", tabIndex: 0, mode: "primary" },
                        { label: "Biography", tabIndex: 2, mode: "secondary" },
                    ]}
                />

                <FastAccessSegment
                    title={"User"}
                    label={"Enter Email"}
                    GridItemWidth={gridItemWidth}
                    GridTitleWidth={gridTitleWidth}
                    callbackFunction={userCallback}
                    isLoading={userLoading}
                    isError={userError || userNotFound}
                    buttons={userButtonList}
                />
            </Stack>

            {openCreateSession && (
                <CreateSessionDrawer
                    open={openCreateSession}
                    onClose={handleClose}
                    showSnackbar={setSnackbar}
                />
            )}

            {openCreateUser && (
                <CreateNewUserDrawer
                    open={openCreateUser}
                    onClose={handleClose}
                    showSnackbar={setSnackbar}
                />
            )}

            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            ) : null}
        </Box>
    );
};

export default FastAccess;
